// src/pages/programme/ProgrammePage.tsx
// The programme officer's review: one cohort's applications, each with the
// automatic checks beside it. Approve gives ₦200,000 of credit (once per
// startup, ever); a rejection's reason stays internal.
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, HelpCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { getErrorMessage } from '@/lib/api';
import { useRoles } from '@/hooks/useRoles';
import {
  REJECT_REASONS, formatDay, programmeApi,
  type ApplicationDetail, type ApplicationRow, type CheckKey, type CheckResult, type ProgrammeCohort,
} from '@/lib/startups';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { fieldControl, fieldControlSm, fieldLabel, fieldTextarea } from '@/components/ui/fieldStyles';

const CHECKS: { key: CheckKey; label: string }[] = [
  { key: 'email', label: 'Email on the domain' },
  { key: 'site', label: 'Site live' },
  { key: 'domainAge', label: 'Domain age' },
  { key: 'ranking', label: 'Ranking' },
  { key: 'address', label: 'Address on the map' },
  { key: 'accelerator', label: 'Accelerator' },
  { key: 'duplicate', label: 'Duplicates' },
];
const STATUS_LABEL = { waiting: 'Waiting', approved: 'Approved', rejected: 'Rejected' } as const;
const STATUS_TONE: Record<keyof typeof STATUS_LABEL, BadgeTone> = { waiting: 'warning', approved: 'success', rejected: 'neutral' };

const CheckIcon: React.FC<{ ok: boolean | null | undefined }> = ({ ok }) =>
  ok === true ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-label="Passed" />
    : ok === false ? <XCircle className="h-4 w-4 shrink-0 text-error" aria-label="Failed" />
    : <HelpCircle className="h-4 w-4 shrink-0 text-muted-foreground" aria-label="Couldn't check" />;

const Row: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <><dt className="text-muted-foreground">{k}</dt><dd className="mb-2 break-words text-foreground sm:mb-0">{children}</dd></>
);

const ProgrammePage: React.FC = () => {
  const qc = useQueryClient();
  const { isAdmin } = useRoles();
  const [cohort, setCohort] = useState<string>('');
  const [selected, setSelected] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  const cohorts = useQuery<{ cohorts: ProgrammeCohort[] }>({ queryKey: ['programme-cohorts'], queryFn: programmeApi.cohorts });
  useEffect(() => {
    if (!cohort && cohorts.data?.cohorts[0]) setCohort(cohorts.data.cohorts[0].id);
  }, [cohort, cohorts.data]);
  const list = useQuery<{ applications: ApplicationRow[] }>({
    queryKey: ['programme-applications', cohort], queryFn: () => programmeApi.list(cohort), enabled: !!cohort,
  });
  const detail = useQuery<ApplicationDetail>({
    queryKey: ['programme-application', selected], queryFn: () => programmeApi.get(selected!), enabled: !!selected,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['programme-cohorts'] });
    qc.invalidateQueries({ queryKey: ['programme-applications'] });
    qc.invalidateQueries({ queryKey: ['programme-application'] });
  };
  const decide = useMutation({
    mutationFn: (kind: 'approve' | 'reject' | 'reopen') =>
      kind === 'approve' ? programmeApi.approve(selected!) : kind === 'reject' ? programmeApi.reject(selected!, reason, note) : programmeApi.reopen(selected!),
    onSuccess: (_d, kind) => {
      toast.success(kind === 'approve' ? 'Approved. ₦200,000 of credit given.' : kind === 'reject' ? 'Rejected.' : 'Back to waiting.');
      setConfirm(null); setReason(''); setNote('');
      refresh();
    },
    onError: (e) => { toast.error(getErrorMessage(e)); refresh(); },
  });

  const current = cohorts.data?.cohorts.find(c => c.id === cohort);
  const a = detail.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Startup programme"
        description="Read each application and decide. Approving gives ₦200,000 of credit."
        actions={cohorts.data && cohorts.data.cohorts.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor="cohort" className="text-sm font-medium text-foreground">Cohort</label>
            <select id="cohort" className={`${fieldControlSm} w-auto min-w-36`} value={cohort} onChange={e => { setCohort(e.target.value); setSelected(null); }}>
              {cohorts.data.cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      />

      {cohorts.data?.cohorts.length === 0 && (
        <Panel><EmptyState title="No cohorts yet." description="An admin creates them at Admin, Startups." /></Panel>
      )}
      {current && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
          <span>{formatDay(current.opens_at)} to {formatDay(current.closes_at)}</span>
          <span className="flex flex-wrap gap-2">
            <Badge tone="warning">Waiting <span className="font-mono tabular-nums">{current.waiting}</span></Badge>
            <Badge tone="success">Approved <span className="font-mono tabular-nums">{current.approved}</span></Badge>
            <Badge>Rejected <span className="font-mono tabular-nums">{current.rejected}</span></Badge>
          </span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <Panel title="Applications" bodyClassName="p-2 pt-1">
          {list.isLoading && !!cohort && <SkeletonLines count={4} lineClassName="h-14 rounded-xl" className="p-1" />}
          {list.data?.applications.length === 0 && <p className="px-3 pb-3 pt-1 text-sm text-muted-foreground">No applications yet.</p>}
          <ul className="space-y-0.5">
            {list.data?.applications.map(r => (
              <li key={r.id}>
                <button type="button" onClick={() => setSelected(r.id)}
                  aria-current={selected === r.id ? 'true' : undefined}
                  className={`w-full rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${selected === r.id ? 'bg-surface-raised ring-1 ring-inset ring-border' : 'hover:bg-surface-raised/60'}`}>
                  <span className="block truncate font-medium text-foreground">{r.business_name}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {r.product_domain ?? r.company_domain} · {STATUS_LABEL[r.status]}
                    {r.checks_summary ? ` · ${r.checks_summary.ok} of ${r.checks_summary.total} checks` : ' · checks running'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <section className="space-y-6 rounded-2xl border border-border bg-surface p-5" aria-label="Application">
          {!selected && <EmptyState title="Choose an application." description="Its details and automatic checks show here." />}
          {selected && detail.isLoading && (
            <div aria-hidden="true" className="space-y-3"><div className="skeleton h-6 w-56" /><div className="skeleton h-4 w-80 max-w-full" /><SkeletonLines count={5} lineClassName="h-5" className="pt-3" /></div>
          )}
          {a && (
            <>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold tracking-tight text-foreground">{a.business_name}</h2>
                  <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {STATUS_LABEL[a.status]}{a.decided_by_email ? ` by ${a.decided_by_email}` : ''} · applied {formatDay(a.created_at)} by {a.email}
                </p>
                {a.status === 'rejected' && a.reject_reason && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Internal reason: {REJECT_REASONS.find(r => r.id === a.reject_reason)?.label ?? a.reject_reason}{a.reject_note ? `. ${a.reject_note}` : ''}
                  </p>
                )}
              </div>

              <div className="rounded-xl border border-border p-4">
                <h3 className="text-sm font-medium text-foreground">Automatic checks</h3>
                {!a.checks && <p className="mt-1 text-sm text-muted-foreground">The checks are still running. Refresh in a minute.</p>}
                {a.checks && (
                  <ul className="mt-3 space-y-2 text-sm">
                    {CHECKS.map(({ key, label }) => {
                      const c: CheckResult | undefined = a.checks?.[key];
                      return (
                        <li key={key} className="flex items-start gap-2">
                          <span className="mt-0.5"><CheckIcon ok={c?.ok} /></span>
                          <span><span className="text-foreground">{label}:</span> <span className="text-muted-foreground">{c?.detail ?? "couldn't check"}</span></span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <dl className="grid gap-x-4 gap-y-2.5 text-sm sm:grid-cols-[11rem_minmax(0,1fr)]">
                <Row k="Registration"><span className="font-mono text-[13px]">{a.reg_country} · {a.reg_number}</span> <span className="text-muted-foreground">(check with the registry)</span></Row>
                <Row k="Address">
                  {a.address_label ?? a.address_text}
                  {a.address_lat != null && a.address_lon != null && (
                    <> · <a className="text-accent-text hover:underline" target="_blank" rel="noreferrer"
                      href={`https://maps.latlens.com/@${a.address_lat.toFixed(5)},${a.address_lon.toFixed(5)},17z`}>See it on Latlens Maps</a></>
                  )}
                </Row>
                <Row k="Domains">
                  {[a.company_domain, a.product_domain].filter(Boolean).map((d, i) => (
                    <React.Fragment key={d}>{i > 0 && ', '}<a className="text-accent-text hover:underline" href={`https://${d}`} target="_blank" rel="noreferrer">{d}</a></React.Fragment>
                  ))}
                </Row>
                <Row k="Founder">
                  {a.founder_name} · <a className="text-accent-text hover:underline" href={a.founder_linkedin} target="_blank" rel="noreferrer">LinkedIn</a>
                  {(a.cofounder_linkedins ?? []).map((l, i) => (
                    <React.Fragment key={l}> · <a className="text-accent-text hover:underline" href={l} target="_blank" rel="noreferrer">Co-founder {i + 1}</a></React.Fragment>
                  ))}
                </Row>
                <Row k="Customers in">{a.customer_countries.join(', ')}</Row>
                <Row k="Stage">
                  {a.stage === 'building' ? `Building one: ${a.mvp_plan ?? ''}` : (
                    <>{a.stage === 'live' ? 'Live product' : 'MVP'} · <a className="text-accent-text hover:underline" href={a.product_link ?? '#'} target="_blank" rel="noreferrer">{a.product_link}</a></>
                  )}
                </Row>
                <Row k="Accelerator">
                  {a.accelerator_name ?? (a.accelerator_other ? `Other: ${a.accelerator_other}` : 'None')}{a.accelerator_year ? ` (${a.accelerator_year})` : ''}
                </Row>
                <Row k="Pitch"><span className="whitespace-pre-line">{a.pitch}</span></Row>
              </dl>

              {(a.status === 'waiting' || (a.status === 'rejected' && isAdmin)) && (
                <div className="flex flex-wrap gap-3 border-t border-border pt-5">
                  {a.status === 'waiting' && (
                    <>
                      <Button onClick={() => setConfirm('approve')}>Approve</Button>
                      <Button variant="outline" onClick={() => setConfirm('reject')}>Reject</Button>
                    </>
                  )}
                  {a.status === 'rejected' && isAdmin && (
                    <Button variant="outline" onClick={() => decide.mutate('reopen')} disabled={decide.isPending}>Reopen</Button>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <Modal isOpen={confirm === 'approve'} onClose={() => setConfirm(null)} title={`Approve ${a?.business_name ?? ''}`} size="sm">
        <p className="text-sm text-muted-foreground">
          This gives {a?.email} <strong className="text-foreground">₦200,000 of credit</strong> (2,000,000 credits) straight away. It can't be undone.
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
          <Button onClick={() => decide.mutate('approve')} disabled={decide.isPending}>Approve and give credit</Button>
        </div>
      </Modal>

      <Modal isOpen={confirm === 'reject'} onClose={() => setConfirm(null)} title={`Reject ${a?.business_name ?? ''}`} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">The startup won't see this.</p>
          <div>
            <label htmlFor="reject-reason" className={fieldLabel}>Reason</label>
            <select id="reject-reason" className={fieldControl} value={reason} onChange={e => setReason(e.target.value)}>
              <option value="">Choose a reason</option>
              {REJECT_REASONS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="reject-note" className={fieldLabel}>Note (optional)</label>
            <textarea id="reject-note" className={fieldTextarea} rows={3} maxLength={1000} value={note} onChange={e => setNote(e.target.value)} />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => decide.mutate('reject')} disabled={!reason || decide.isPending}>Reject application</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProgrammePage;
